import { toPersianDigits } from '@clinic/money-date';
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * SMS rules (BR-17, BR-18). The appointment templates and their timings come from the legacy app
 * (docs/legacy/reference-data.md §2); `recall` is new (master-spec §Y, idea 5.1). Since ADR-052 the
 * booking confirmation is its own template, and the touch-up reminder (`repair_reminder`) goes out
 * on a service line's touch-up date, like renewal.
 */
export const SMS_TEMPLATE_KEYS = [
  'booking_confirmation',
  'appointment_reminder',
  'repair_reminder',
  'post_procedure',
  'recall',
] as const;
export type SmsTemplateKey = (typeof SMS_TEMPLATE_KEYS)[number];
export const SmsTemplateKeySchema = z.enum(SMS_TEMPLATE_KEYS);

/**
 * `appointment`: timed before the appointment starts. `visit`: on a date of a done service (its
 * renewal or touch-up date), from a completed visit or a recorded past service.
 */
export const SMS_TEMPLATE_KIND: Readonly<Record<SmsTemplateKey, 'appointment' | 'visit'>> = {
  booking_confirmation: 'appointment',
  appointment_reminder: 'appointment',
  repair_reminder: 'visit',
  post_procedure: 'appointment',
  recall: 'visit',
};

export const SMS_OFFSETS = ['book', '72h', '24h', '12h', '6h'] as const;
export type SmsOffset = (typeof SMS_OFFSETS)[number];

/** Legacy `whenHours`: `book` means "as soon as it is booked". */
export const SMS_OFFSET_HOURS: Readonly<Record<SmsOffset, number | null>> = {
  book: null,
  '72h': 72,
  '24h': 24,
  '12h': 12,
  '6h': 6,
};

/**
 * A template's timing is `book` or a whole number of hours written `<n>h`: the four legacy presets
 * are only the usual values, a clinic may type its own (1 to `SMS_OFFSET_MAX_HOURS`).
 */
export const SMS_OFFSET_MAX_HOURS = 720;
export const SMS_OFFSET_MAX_COUNT = 8;
const SMS_OFFSET_PATTERN = /^[1-9][0-9]{0,2}h$/;

/** Hours of one timing; null for `book` or anything that is not a valid `<n>h`. */
export function smsOffsetHours(offset: string): number | null {
  if (!SMS_OFFSET_PATTERN.test(offset)) return null;
  const hours = Number.parseInt(offset, 10);
  return hours <= SMS_OFFSET_MAX_HOURS ? hours : null;
}
export function isSmsOffset(offset: string): boolean {
  return offset === 'book' || smsOffsetHours(offset) !== null;
}
export const smsOffsetOf = (hours: number): string => `${hours}h`;

/**
 * Follow-up care goes out AFTER the appointment (its timings count hours since it ended), so it
 * has no `book`. Every other appointment template counts hours before the start.
 */
export const SMS_AFTER_TEMPLATE_KEYS = ['post_procedure'] as const;
export function isSmsAfterTemplate(key: string): boolean {
  return (SMS_AFTER_TEMPLATE_KEYS as readonly string[]).includes(key);
}
/** The usual follow-up timings, in hours after the appointment. */
export const SMS_AFTER_PRESETS = ['24h', '48h', '72h', '168h'] as const;
/** A follow-up message stays in the queue this long after it becomes due. */
export const SMS_AFTER_WINDOW_DAYS = 7;

/** Legacy `dueTemplates` rule for one template: due if booked-now, or within one of its windows. */
export function isAppointmentTemplateDue(offsets: readonly string[], hoursLeft: number): boolean {
  return offsets.some((k) => {
    if (k === 'book') return true;
    const h = smsOffsetHours(k);
    return h !== null && hoursLeft <= h;
  });
}

/** A follow-up template is due once the first of its timings has passed since the appointment ended. */
export function isFollowUpTemplateDue(offsets: readonly string[], hoursSince: number): boolean {
  return offsets.some((k) => {
    const h = smsOffsetHours(k);
    return h !== null && hoursSince >= h;
  });
}

/**
 * Legacy `dueTemplates`: the appointment's own selection (an empty one also means the defaults),
 * otherwise every template that has a timing; of those, the ones due now. Pass active templates only.
 */
export function dueAppointmentTemplates<T extends { id: string; offsets: readonly string[] }>(
  templates: readonly T[],
  selectedIds: readonly string[] | null,
  hoursLeft: number,
): T[] {
  const ids =
    selectedIds && selectedIds.length > 0
      ? selectedIds
      : templates.filter((t) => t.offsets.length > 0).map((t) => t.id);
  return templates.filter((t) => ids.includes(t.id) && isAppointmentTemplateDue(t.offsets, hoursLeft));
}

/** How long before an appointment's start it still shows in the queue (legacy: one hour). */
export const SMS_QUEUE_GRACE_HOURS = 1;
/** A renewal message stays in the queue this long after it becomes due. */
export const SMS_RECALL_WINDOW_DAYS = 15;
/** A touch-up message stays in the queue this long after it becomes due. */
export const SMS_TOUCH_UP_WINDOW_DAYS = 7;

export interface SmsFillValues {
  title: string;
  name: string;
  procedure: string;
  time: string;
  date: string;
  clinic: string;
}

/** What a template may contain in braces; anything else would reach the patient as typed. */
export const SMS_PLACEHOLDERS = ['{عنوان}', '{نام}', '{پروسیجر}', '{ساعت}', '{تاریخ}', '{کلینیک}'] as const;

/** Legacy `fillTpl`, plus `{کلینیک}` so the clinic name is no longer hard-coded. */
export function fillSmsTemplate(body: string, v: SmsFillValues): string {
  return body
    .split('{عنوان}')
    .join(v.title)
    .split('{نام}')
    .join(v.name)
    .split('{پروسیجر}')
    .join(v.procedure)
    .split('{ساعت}')
    .join(v.time)
    .split('{تاریخ}')
    .join(v.date)
    .split('{کلینیک}')
    .join(v.clinic);
}

/** Legacy `patientTitle`: gender was stored as the title itself, unknown gender reads «آقا / خانم». */
export function titleForGender(gender: 'female' | 'male' | null): string {
  if (gender === 'male') return 'آقا';
  return gender === 'female' ? 'خانم' : 'آقا / خانم';
}

export const SmsTemplateSchema = z.object({
  id: UuidSchema,
  key: z.enum(SMS_TEMPLATE_KEYS),
  kind: z.enum(['appointment', 'visit']),
  title: z.string(),
  body: z.string(),
  offsets: z.array(z.string()),
  isActive: z.boolean(),
  /** true when the clinic has its own copy of the system template. */
  isCustomized: z.boolean(),
});
export type SmsTemplate = z.infer<typeof SmsTemplateSchema>;
export const SmsTemplateListResponseSchema = z.object({ items: z.array(SmsTemplateSchema) });

export const SmsTemplateUpdateInputSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, { message: 'متن پیامک را بنویسید.' })
    .max(500, { message: 'متن پیامک حداکثر ۵۰۰ نویسه است.' })
    // A typo such as {نم} is not filled in: the patient would receive the braces as they are.
    .superRefine((body, ctx) => {
      const unknown = (body.match(/\{[^{}]*\}/g) ?? []).filter(
        (token) => !(SMS_PLACEHOLDERS as readonly string[]).includes(token),
      );
      if (unknown.length > 0) {
        ctx.addIssue({
          code: 'custom',
          message: `«${unknown[0]}» شناخته نمی‌شود. جای‌گذاری‌های مجاز: ${SMS_PLACEHOLDERS.join(' ')}`,
        });
      }
    }),
  offsets: z
    .array(
      z.string().refine(isSmsOffset, {
        message: `زمان ارسال را به ساعت و عدد صحیح از ۱ تا ${toPersianDigits(SMS_OFFSET_MAX_HOURS)} بنویسید.`,
      }),
    )
    .max(SMS_OFFSET_MAX_COUNT, { message: `حداکثر ${toPersianDigits(SMS_OFFSET_MAX_COUNT)} زمان ارسال.` })
    .refine((v) => new Set(v).size === v.length, { message: 'زمان تکراری است.' })
    .default([]),
  isActive: z.boolean(),
});
export type SmsTemplateUpdateInput = z.input<typeof SmsTemplateUpdateInputSchema>;

export const SMS_OUTCOMES = ['sent', 'skipped'] as const;

/**
 * How a message leaves (ADR-029). `device`: the user's own SIM after they confirm (BR-18).
 * `operator`: the platform's SMS gateway, a paid option the platform enables per clinic.
 */
export const SMS_CHANNELS = ['device', 'operator'] as const;
export type SmsChannel = (typeof SMS_CHANNELS)[number];

/** `coming_soon`: the platform has not switched the channel on. `not_enabled`: this clinic has not. */
export const SMS_CHANNEL_UNAVAILABLE_REASONS = ['coming_soon', 'not_enabled'] as const;

export const SmsChannelStatusSchema = z.object({
  key: z.enum(SMS_CHANNELS),
  available: z.boolean(),
  unavailableReason: z.enum(SMS_CHANNEL_UNAVAILABLE_REASONS).nullable(),
});
export type SmsChannelStatus = z.infer<typeof SmsChannelStatusSchema>;
export const SmsChannelListResponseSchema = z.object({ items: z.array(SmsChannelStatusSchema) });

export const SmsQueueItemSchema = z.object({
  kind: z.enum(['appointment', 'recall']),
  templateId: UuidSchema,
  templateKey: z.enum(SMS_TEMPLATE_KEYS),
  templateTitle: z.string(),
  /**
   * appointment id (kind appointment); for kind recall (a renewal or touch-up date) the visit item id,
   * or the past service id (ADR-052).
   */
  sourceId: UuidSchema,
  patientId: UuidSchema,
  patientName: z.string(),
  phone: z.string().nullable(),
  body: z.string(),
  appointmentStartsAt: IsoDateTimeSchema.nullable(),
  dueAt: IsoDateTimeSchema.nullable(),
  outcome: z.enum(SMS_OUTCOMES).nullable(),
  /** How a recorded message left: null until it is marked or sent. */
  channel: z.enum(SMS_CHANNELS).nullable(),
  markedAt: IsoDateTimeSchema.nullable(),
  /** When the follow-up was closed (the client came, or no message was needed); kind recall only. */
  closedAt: IsoDateTimeSchema.nullable(),
});
export type SmsQueueItem = z.infer<typeof SmsQueueItemSchema>;
export const SmsQueueResponseSchema = z.object({ items: z.array(SmsQueueItemSchema) });
export type SmsQueueResponse = z.infer<typeof SmsQueueResponseSchema>;

export const SmsMarkInputSchema = z.object({
  templateId: UuidSchema,
  sourceId: UuidSchema,
  outcome: z.enum(SMS_OUTCOMES),
});
export type SmsMarkInput = z.input<typeof SmsMarkInputSchema>;

/** Closes a renewal or touch-up follow-up, whether or not its message was sent. */
export const SmsCloseInputSchema = z.object({ templateId: UuidSchema, sourceId: UuidSchema });
export type SmsCloseInput = z.input<typeof SmsCloseInputSchema>;

/** Sends one queue message through the operator channel (the server renders the text itself). */
export const SmsSendInputSchema = z.object({ templateId: UuidSchema, sourceId: UuidSchema });
export type SmsSendInput = z.input<typeof SmsSendInputSchema>;

/** Users often type their name with the title already in it. */
function withoutDoctorTitle(name: string): string {
  return name.trim().replace(/^(دکتر|Dr\.?)\s+/i, '');
}

/**
 * The nightly message to a practitioner about tomorrow's appointments (ADR-029), in the wording
 * the product owner gave. It carries counts per service and a link: never a patient's name.
 */
export function formatPractitionerDigest(input: {
  doctorName: string | null;
  lines: readonly { serviceName: string; count: number }[];
  link: string;
}): string {
  const name = input.doctorName ? withoutDoctorTitle(input.doctorName) : '';
  return [
    `جناب دکتر${name ? ` ${name}` : ''}، نوبت‌های روز آینده‌ی شما به این صورت می‌باشند:`,
    ...input.lines.map((l) => `${toPersianDigits(l.count)} نوبت خدمت ${l.serviceName}`),
    'برای مشاهده‌ی جزئیات نوبت‌ها به لینک زیر بروید:',
    input.link,
  ].join('\n');
}
